import { cloneElement, useEffect, useState } from "react";
import { Head, Link, router, useForm, usePage } from "@inertiajs/react";
import AuthLayout from "@/layouts/auth-layout";
import { useConfirmDialog } from "@/components/confirm-dialog-provider";
import Drawer from "@/components/drawer";
import ProductCreateDrawer from "@/pages/products/product-create-drawer";
import ProductImportDrawer from "@/pages/manage/products/product-import-drawer";
import {
  add_to_current_organization_manage_product_path,
  duplicate_manage_product_path,
  export_manage_product_path,
  manage_product_path,
  manage_products_path,
  product_path,
  remove_from_current_organization_manage_product_path
} from "@/routes";

function ManageProductsIndex({ products, onCreateProduct = null }) {
  const { current_tenant: currentTenant } = usePage().props;
  const { confirm } = useConfirmDialog();
  const { post: duplicateProduct, processing: isDuplicating } = useForm({});

  const toggleCurrentOrganization = async (product) => {
    if (!currentTenant?.id) return;

    const removing = product.available_in_current_tenant;
    const confirmed = await confirm({
      title: removing ? "Fjern produkt fra organisation" : "Tilføj produkt til organisation",
      description: `Vil du ${removing ? "fjerne" : "tilføje"} "${product.name}" ${removing ? "fra" : "til"} ${currentTenant.name}?`,
      confirmLabel: removing ? "Fjern" : "Tilføj",
      confirmVariant: removing ? "danger" : "primary"
    });

    if (!confirmed) return;

    if (removing) {
      router.delete(remove_from_current_organization_manage_product_path(product.id), {
        preserveScroll: true
      });
      return;
    }

    router.post(add_to_current_organization_manage_product_path(product.id), {}, {
      preserveScroll: true
    });
  };

  const deleteProduct = async (product) => {
    const confirmed = await confirm({
      title: "Slet produkt",
      description: `Vil du slette "${product.name}" fra det globale katalog?`,
      confirmLabel: "Slet",
      confirmVariant: "danger"
    });

    if (!confirmed) return;

    router.delete(`${manage_product_path(product.id)}?return_to=manage_index`, {
      preserveScroll: true
    });
  };

  return (
    <>
      <Head title="Administrer produkter" />

      <section className="workspace-content">
        {products.length === 0 ? (
          <div className="workspace-empty">
            <h3 className="workspace-empty__title">Ingen produkter i kataloget endnu</h3>
            <p className="workspace-empty__copy">
              Opret det første globale produkt og gør det tilgængeligt for organisationerne.
            </p>
            <button type="button" className="btn btn-primary mt-3" onClick={onCreateProduct}>
              Opret første produkt
            </button>
          </div>
        ) : (
          <div className="row g-4">
            {products.map((product) => (
                <div key={product.id} className="col-12">
                  <article className="card h-100">
                  <div className="card-body workspace-card-stack">
                    <div className="d-flex align-items-start justify-content-between gap-4 flex-wrap">
                      <div>
                        <div className="workspace-card-eyebrow">{product.tag}</div>
                        <h3 className="workspace-card-title">{product.name}</h3>
                      </div>

                      <div className="d-flex flex-wrap gap-3">
                        <span className="workspace-chip">{product.steps_count} trin</span>
                        <span className="workspace-chip">{product.templates_count} skabeloner</span>
                        <span className="workspace-chip">{product.organizations_count} organisationer</span>
                      </div>
                    </div>

                    <p className="workspace-card-description">
                      {product.description || "Et globalt produkt i det delte katalog for efterlevelse."}
                    </p>

                    <div className="workspace-card-stack__footer workspace-card-stack__actions">
                      <Link href={`${manage_product_path(product.id)}/edit`} className="btn btn-primary">
                        Redigér
                      </Link>
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        disabled={isDuplicating}
                        onClick={() => duplicateProduct(duplicate_manage_product_path(product.id))}
                      >
                        Duplikér
                      </button>
                      <a href={export_manage_product_path(product.id)} className="btn btn-outline-primary" download>
                        Eksportér JSON
                      </a>
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => deleteProduct(product)}
                      >
                        Slet
                      </button>
                      {currentTenant?.id ? (
                        <button
                          type="button"
                          className={`btn ${product.available_in_current_tenant ? "btn-outline-danger" : "btn-outline-primary"}`}
                          onClick={() => toggleCurrentOrganization(product)}
                        >
                          {product.available_in_current_tenant ? `Fjern fra ${currentTenant.name}` : `Tilføj til ${currentTenant.name}`}
                        </button>
                      ) : null}
                      {product.available_in_current_tenant ? (
                        <Link href={product_path(product.id)} className="btn btn-outline-secondary">
                          Åbn i arbejdsrum
                        </Link>
                      ) : (
                        <span className="btn btn-outline-secondary disabled">Ikke i aktiv organisation</span>
                      )}
                    </div>
                  </div>
                </article>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function ManageProductsIndexLayout({ page }) {
  const {
    new_product: newProduct = null,
    create_errors: createErrors = [],
    show_create_product_drawer: showCreateProductDrawer = false,
    import_errors: importErrors = [],
    show_import_product_drawer: showImportProductDrawer = false,
    layout_header: layoutHeader,
  } = page.props;

  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(showCreateProductDrawer);
  const [isCreateDrawerSubmitting, setIsCreateDrawerSubmitting] = useState(false);
  const [isImportDrawerOpen, setIsImportDrawerOpen] = useState(showImportProductDrawer);
  const [isImportDrawerSubmitting, setIsImportDrawerSubmitting] = useState(false);

  useEffect(() => {
    setIsCreateDrawerOpen(showCreateProductDrawer);
  }, [showCreateProductDrawer]);

  useEffect(() => {
    setIsImportDrawerOpen(showImportProductDrawer);
  }, [showImportProductDrawer]);

  return (
    <AuthLayout
      header={{
        breadcrumbs: [{ label: "Administration", href: manage_products_path() }, { label: "Produkter" }],
        title: layoutHeader?.title || "Global produktkatalog",
        actions: [
          {
            onClick: () => setIsImportDrawerOpen(true),
            label: "Importér JSON",
            variant: "outline-primary"
          },
          {
            onClick: () => setIsCreateDrawerOpen(true),
            label: "Opret",
            variant: "primary"
          }
        ]
      }}
    >
      {cloneElement(page, { onCreateProduct: () => setIsCreateDrawerOpen(true) })}

      <Drawer
        isOpen={isCreateDrawerOpen}
        onClose={() => setIsCreateDrawerOpen(false)}
        title="Opret produkt"
        size="md"
        closeDisabled={isCreateDrawerSubmitting}
      >
        <ProductCreateDrawer
          product={newProduct}
          errors={createErrors}
          onSuccess={() => setIsCreateDrawerOpen(false)}
          onProcessingChange={setIsCreateDrawerSubmitting}
        />
      </Drawer>

      <Drawer
        isOpen={isImportDrawerOpen}
        onClose={() => setIsImportDrawerOpen(false)}
        title="Importér produkt"
        size="md"
        closeDisabled={isImportDrawerSubmitting}
      >
        <ProductImportDrawer
          errors={importErrors}
          onSuccess={() => setIsImportDrawerOpen(false)}
          onProcessingChange={setIsImportDrawerSubmitting}
        />
      </Drawer>

    </AuthLayout>
  );
}

ManageProductsIndex.layout = (page) => <ManageProductsIndexLayout page={page} />;

export default ManageProductsIndex;
